import { useState, useCallback } from "react";
import { useCreate, useNotify, useRefresh } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import { X, Plus, Loader2, FileText } from "lucide-react";

interface CreateInvoiceDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (invoiceId: string) => void;
  contactId: string | number;
  jobId?: string;
  jobTitle?: string;
  estimateId?: string;
  defaultTotal?: number;
}

export function CreateInvoiceDialog({
  open,
  onClose,
  onCreated,
  contactId,
  jobId,
  jobTitle,
  estimateId,
  defaultTotal,
}: CreateInvoiceDialogProps) {
  const [create] = useCreate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [creating, setCreating] = useState(false);

  const [form, setForm] = useState({
    title: jobTitle || "",
    description: "",
    invoice_type: "standard" as string,
    total: defaultTotal || 0,
    tax_rate: 0,
  });

  const handleCreate = useCallback(async () => {
    if (!form.title.trim()) {
      notify("Title is required", { type: "warning" });
      return;
    }
    setCreating(true);
    try {
      const subtotal = form.total / (1 + form.tax_rate);
      const taxAmount = form.total - subtotal;

      const result = await create(
        "invoices",
        {
          data: {
            contact_id: contactId,
            job_id: jobId || null,
            estimate_id: estimateId || null,
            title: form.title.trim(),
            description: form.description.trim(),
            invoice_type: form.invoice_type,
            subtotal: Math.round(subtotal * 100) / 100,
            tax_rate: form.tax_rate,
            tax_amount: Math.round(taxAmount * 100) / 100,
            total: form.total,
            line_items_json: [
              {
                description: form.title.trim(),
                quantity: 1,
                unit_price: form.total,
                amount: form.total,
              },
            ],
          },
        },
        { returnPromise: true }
      );

      // Log event
      await create(
        "events",
        {
          data: {
            contact_id: contactId,
            job_id: jobId || null,
            event_type: "invoice_created",
            payload: {
              invoice_id: (result as { data?: { id?: string } })?.data?.id,
              title: form.title.trim(),
              total: form.total,
              invoice_type: form.invoice_type,
            },
          },
        },
        { returnPromise: true }
      );

      notify("Invoice created", { type: "success" });
      refresh();
      const newId = (result as { data?: { id?: string } })?.data?.id;
      if (newId) onCreated(newId);
      onClose();
    } catch (err) {
      notify(`Failed: ${(err as Error).message}`, { type: "error" });
    } finally {
      setCreating(false);
    }
  }, [form, contactId, jobId, estimateId, create, notify, refresh, onCreated, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <Card className="w-full max-w-md mx-4 shadow-xl">
        <CardContent className="p-0">
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-base font-semibold flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Create Invoice
            </h3>
            <button
              onClick={onClose}
              className="p-1 rounded hover:bg-gray-100 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="p-4 space-y-3">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">
                Title *
              </label>
              <input
                type="text"
                value={form.title}
                onChange={(e) =>
                  setForm((f) => ({ ...f, title: e.target.value }))
                }
                placeholder="e.g. Kitchen remodel deposit"
                className="w-full px-3 py-2 border rounded text-sm"
              />
            </div>

            <div>
              <label className="text-xs text-muted-foreground block mb-1">
                Description
              </label>
              <textarea
                value={form.description}
                onChange={(e) =>
                  setForm((f) => ({ ...f, description: e.target.value }))
                }
                rows={2}
                className="w-full px-3 py-2 border rounded text-sm resize-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">
                  Invoice type
                </label>
                <select
                  value={form.invoice_type}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, invoice_type: e.target.value }))
                  }
                  className="w-full px-3 py-2 border rounded text-sm"
                >
                  <option value="deposit">Deposit</option>
                  <option value="progress">Progress</option>
                  <option value="final">Final</option>
                  <option value="standard">Standard</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">
                  Total ($)
                </label>
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  value={form.total}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      total: parseFloat(e.target.value) || 0,
                    }))
                  }
                  className="w-full px-3 py-2 border rounded text-sm text-right"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 p-4 border-t bg-gray-50 rounded-b-lg">
            <button
              onClick={onClose}
              disabled={creating}
              className="px-3 py-1.5 rounded text-sm font-medium border text-gray-700 hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={creating || !form.title.trim() || form.total <= 0}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {creating ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Plus className="w-3.5 h-3.5" />
              )}
              Create Invoice
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
